import { useApolloClient, useQuery } from '@apollo/client';
import { Splitter } from 'antd';
import classNames from 'classnames';
import { debounce, isEqual, mapValues, noop, property, some } from 'lodash-es';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Route, useRouteMatch } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import {
  monitorFiltersFormToGraphQL,
  monitorSortFormToGraphQL,
} from '../app/data/filterConversions';
import { QuoteStage } from '../app/enums/QuoteStage';
import { ShipmentStage } from '../app/enums/ShipmentStage';
import { View } from '../app/enums/ViewMapMode';
import { JOBS_COUNTS_QUERY, JOBS_QUERY } from '../app/graphql/jobQueries';
import { QUOTES_QUERY } from '../app/graphql/quoteQueries';
import routes from '../app/routes';
import { extractGraphqlEntity } from '../common/utils/graphqlUtils';
import {
  useDebouncedVariable,
  useMountAndUpdateEffect,
  useMountEffect,
} from '../common/utils/hookUtils';
import {
  FiltersAndSortContext,
  useFiltersAndSort,
} from '../components/data/FiltersAndSortProvider';
import {
  useClearCacheOnVariablesChange,
  usePaginatedQuery,
} from '../components/data/dataLoaders';
import { useFlashMessageContext } from '../components/dialogs/FlashMessageProvider';
import { FlexCol, TitleWithExtra } from '../components/layout/layoutElements';
import { useLayoutElementsStyles } from '../components/layout/layoutElements.styles';
import {
  CollapsibleMapCard,
  CollapsibleMapCardWithToggle,
} from '../components/maps/CollapsibleMapCard';
import { useResponsiveQueries } from '../components/responsive/responsiveQueries';
import { DEFAULT_POLL_REFRESH_INTERVAL } from '../config/constants';
import { useAccounts, useSettings } from '../hooks/data/auth';
import { cssVariables } from '../styles/cssVariables';
import BaseLoggedPage from '../templates/BaseLoggedPage';
import { useBaseLoggedPageStyles } from '../templates/BaseLoggedPage.styles';
import { msToNumber } from '../utils/cssUtils';
import {
  FreeTextJobSearch,
  FreeTextJobSearchContext,
  useFreeTextJobSearch,
} from '../widgets/FreeTextJobSearch';
import QuoteResultPage from './QuoteResultPage';
import ReleaseShipmentPage from './ReleaseShipmentPage';
import ShipmentDetailPage from './ShipmentDetailPage';
import MonitorFilters from './monitor/MonitorFilters';
import MonitorJobList, { renderJobItem } from './monitor/MonitorJobList';
import MonitorJobMap from './monitor/MonitorJobMap';
import {
  SplitterLeftPanelSizeContext,
  SplitterLeftPanelSizeProvider,
  getMinLeftPanelSize,
} from './monitor/SplitterLeftPanelSizeContext';
import { MonitorContext, MonitorProvider } from './monitor/monitorContexts';
import { VIEWS, useMonitorViews } from './monitor/useMonitorViews';
import { useMonitorPageStyles } from './MonitorPage.styles';

function AutoViewSwitcher({ view, setView }) {
  const views = useMonitorViews();

  useEffect(() => {
    // If the current view became disabled, autoselect the first enabled one
    const currentViewConfig = views.find(({ name }) => name === view);
    if (currentViewConfig?.disabled) {
      const firstEnabledView = views.find(({ disabled }) => !disabled);
      if (firstEnabledView) {
        setView(firstEnabledView.name);
      }
    }
  }, [setView, view, views]);

  return null;
}

function ListView() {
  const { styles } = useMonitorPageStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  return (
    <FlexCol
      className={classNames(layoutStyles.flex1, styles.monitorPageJobs)}
      data-subject="job-list"
      data-role="list"
    >
      <MonitorJobList renderItem={renderJobItem} fixedHeightLayout />
    </FlexCol>
  );
}

function MapView() {
  const { styles } = useMonitorPageStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const splitterWrapperRef = useRef(null);
  const [splitterKey, setSplitterKey] = useState(0);

  const { size, setSize } = useContext(SplitterLeftPanelSizeContext);
  const leftPanelMin = getMinLeftPanelSize();

  // https://github.com/ant-design/ant-design/issues/56171
  // Use key in Splitter component to re-render when window resizes, this issue is fixed in Antd v6.
  useLayoutEffect(() => {
    let ro;
    let lastWidth = 0;

    const debouncedSetKey = debounce(() => {
      setSplitterKey(key => key + 1);
    }, 150);

    if (splitterWrapperRef.current) {
      ro = new ResizeObserver(([entry]) => {
        // Round to avoid sub-pixel (e.g., zoom 70%)
        const newWidth = Math.round(entry.contentRect.width);
        // Only trigger re-render if width actually changed more than 2 pixels
        if (Math.abs(newWidth - lastWidth) > 2) {
          lastWidth = newWidth;
          debouncedSetKey();
        }
      });

      ro.observe(splitterWrapperRef.current);
    }

    return () => {
      debouncedSetKey.cancel();
      ro && ro.disconnect();
    };
  }, []);

  const handleResize = sizes => {
    setSize(sizes[0]);
  };

  return (
    <div
      className={classNames(layoutStyles.flex1, styles.monitorPageJobs)}
      data-subject="job-list"
      data-role="list"
      ref={splitterWrapperRef}
    >
      <Splitter key={splitterKey} onResize={handleResize}>
        <Splitter.Panel
          min={leftPanelMin}
          size={size}
          className={classNames(layoutStyles.flexCol, styles.mapViewFirstPanel)}
        >
          <MonitorJobList renderItem={renderJobItem} fixedHeightLayout />
        </Splitter.Panel>
        <Splitter.Panel
          min="50%"
          className={classNames(layoutStyles.flex1, styles.mapViewSecondPanel)}
        >
          <MonitorJobMap />
        </Splitter.Panel>
      </Splitter>
    </div>
  );
}

function ContentLarge({ view, setView }) {
  return (
    <>
      <MonitorFilters view={view} setView={setView} />
      {view === View.LIST ? <ListView /> : <MapView />}
    </>
  );
}

function ContentMedium({ view, setView }) {
  return (
    <>
      <MonitorJobMap
        renderMap={map => (
          <CollapsibleMapCard expanded={view === View.MAP}>
            {map}
          </CollapsibleMapCard>
        )}
      />
      <MonitorFilters view={view} setView={setView} />
      <MonitorJobList renderItem={renderJobItem} fixedHeightLayout={false} />
    </>
  );
}

function ContentSmall({ view, setView }) {
  return (
    <>
      <MonitorJobMap
        renderMap={map => (
          <CollapsibleMapCardWithToggle
            expanded={view === View.MAP}
            setExpanded={expanded => setView(expanded ? View.MAP : View.LIST)}
          >
            {map}
          </CollapsibleMapCardWithToggle>
        )}
      />
      <MonitorFilters view={view} setView={setView} />
      <MonitorJobList renderItem={renderJobItem} fixedHeightLayout={false} />
    </>
  );
}

function useJobGroupLoader({
  variables: variablesOuter,
  stage,
  query = JOBS_QUERY,
  graphqlEntityName = 'customerShipments',
  graphqlResultTypename = 'ShipmentsResult',
  enablePrefetch = false,
  skip = false,
}) {
  const variables = {
    ...variablesOuter,
    filter: {
      ...variablesOuter.filter,
      stage,
    },
  };

  const { onPageReset, ...data } = usePaginatedQuery({
    query,
    variables,
    options: { skip },
    pollInterval: undefined,
    enablePrefetch,
  });
  useClearCacheOnVariablesChange({
    query,
    variables,
    graphqlEntityName,
    graphqlResultTypename,
    onReset: onPageReset,
  });

  return data;
}

// Custom refetch logic to limit refetching of saved and delivered data with the smallest possible impact on the user
function useCustomRefresh({
  counts: countsOuter,
  filter,
  refreshSaved,
  refreshLive,
  refreshDelivered,
  refreshActive,
  refreshAll,
  isDetailOpen,
}) {
  // The most recent data to be used in interval after asynchronous calls
  // Using normal variables would result in usage of the versions from the time of setting the interval
  const counts = useRef(countsOuter);
  const functions = useRef({
    refreshSaved,
    refreshLive,
    refreshDelivered,
    refreshActive,
    refreshAll,
  });
  useEffect(() => {
    counts.current = countsOuter;
    functions.current = {
      refreshSaved,
      refreshLive,
      refreshDelivered,
      refreshActive,
      refreshAll,
    };
  });
  const { errorMessage } = useFlashMessageContext();
  const client = useApolloClient();

  const executeRefresh = useCallback(async () => {
    try {
      const { data: countsData } = await client.query({
        query: JOBS_COUNTS_QUERY,
        variables: { filter },
        fetchPolicy: 'no-cache',
      });
      const { saved: savedCount, delivered: deliveredCount } =
        extractGraphqlEntity(countsData);

      const fns = functions.current;
      // "Saved" and "delivered" are refreshed only when their count has changed, "live" and "all" are refreshed every time
      if (fns.refreshSaved && savedCount !== counts.current.saved) {
        fns.refreshSaved();
      }
      if (fns.refreshLive) {
        fns.refreshLive();
      }
      if (fns.refreshDelivered && deliveredCount !== counts.current.delivered) {
        fns.refreshDelivered();
      }
      if (fns.refreshActive) {
        fns.refreshActive();
      }
      if (fns.refreshAll) {
        fns.refreshAll();
      }
    } catch (e) {
      errorMessage(e);
    }
  }, [client, errorMessage, filter]);

  useEffect(() => {
    if (isDetailOpen) {
      return noop;
    }
    const interval = setInterval(executeRefresh, DEFAULT_POLL_REFRESH_INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [executeRefresh, isDetailOpen]);
  useMountAndUpdateEffect(
    {
      onUpdate: ([prevIsDetailOpen]) => {
        // Force refresh when the detail is closed
        if (prevIsDetailOpen && !isDetailOpen) {
          executeRefresh();
        }
      },
    },
    [isDetailOpen]
  );
}

function GroupedMonitorDataLoader({
  sort,
  filters,
  isDetailOpen,
  activeSection,
  setActiveSection,
  children,
}) {
  const filter = useMemo(() => monitorFiltersFormToGraphQL(filters), [filters]);
  const variables = { filter, sort };
  const { isQuoteEnabled } = useAccounts();

  const { data: countsData } = useQuery(JOBS_COUNTS_QUERY, {
    variables: { filter: {} },
    pollInterval: DEFAULT_POLL_REFRESH_INTERVAL,
  });
  const baseCountsFromQuery = useMemo(() => {
    const counts = countsData ? extractGraphqlEntity(countsData) : {};
    return mapValues(
      {
        ...counts,
        ...(isQuoteEnabled ? { active: counts.active || 0 } : {}),
      },
      val => ({ totalRows: val || 0 })
    );
  }, [countsData, isQuoteEnabled]);

  const { refresh: refreshSaved, ...saved } = useJobGroupLoader({
    variables,
    stage: ShipmentStage.SAVED,
    enablePrefetch: activeSection === 'saved',
  });
  const { refresh: refreshLive, ...live } = useJobGroupLoader({
    variables,
    stage: ShipmentStage.LIVE,
    enablePrefetch: activeSection === 'live',
  });
  const { refresh: refreshDelivered, ...delivered } = useJobGroupLoader({
    variables,
    stage: ShipmentStage.DELIVERED,
    enablePrefetch: activeSection === 'delivered',
  });

  const quoteFilter = useMemo(
    () => monitorFiltersFormToGraphQL(filters, true),
    [filters]
  );
  const quoteVariables = { filter: quoteFilter, sort };
  const { refresh: refreshActive, ...active } = useJobGroupLoader({
    variables: quoteVariables,
    stage: QuoteStage.ACTIVE,
    query: QUOTES_QUERY,
    graphqlEntityName: 'customerQuotes',
    graphqlResultTypename: 'QuotesResult',
    enablePrefetch: activeSection === 'active',
    skip: !isQuoteEnabled,
  });

  const data = {
    ...(isQuoteEnabled ? { active } : {}),
    saved,
    live,
    delivered,
  };

  useCustomRefresh({
    refreshSaved,
    refreshLive,
    refreshDelivered,
    refreshActive,
    filter,
    counts: mapValues(data, property('totalRows')),
    isDetailOpen,
  });

  return (
    <MonitorProvider
      data={data}
      variables={variables}
      baseCounts={baseCountsFromQuery}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
    >
      {children}
    </MonitorProvider>
  );
}

function SingleGroupMonitorDataLoader({
  sort,
  freeTextJobSearch,
  isDetailOpen,
  children,
}) {
  const filter = useMemo(
    () => ({ jobOrBolNumber: freeTextJobSearch.value }),
    [freeTextJobSearch.value]
  );
  const variables = { filter, sort };
  const { isQuoteEnabled } = useAccounts();

  const { refresh: refreshAll, ...all } = useJobGroupLoader({
    variables,
    enablePrefetch: true,
  });

  const { refresh: refreshActive, ...active } = useJobGroupLoader({
    variables,
    stage: QuoteStage.ACTIVE,
    query: QUOTES_QUERY,
    graphqlEntityName: 'customerQuotes',
    graphqlResultTypename: 'QuotesResult',
    enablePrefetch: true,
    skip: !isQuoteEnabled,
  });

  const showActiveFallback = isQuoteEnabled && all.totalRows <= 0;
  const data = {
    all: {
      ...(showActiveFallback ? active : all),
      loading: all.loading || (isQuoteEnabled && active.loading),
      loadingPage1: all.loadingPage1 || (isQuoteEnabled && active.loadingPage1),
    },
  };

  useCustomRefresh({
    refreshAll: showActiveFallback ? refreshActive : refreshAll,
    filter,
    counts: mapValues(data, property('totalRows')),
    isDetailOpen,
  });

  return (
    <MonitorProvider
      data={data}
      baseCounts={data}
      variables={variables}
      initialSection="all"
    >
      {children}
    </MonitorProvider>
  );
}

function MonitorDataLoader({
  sort,
  filters,
  freeTextJobSearch,
  isDetailOpen,
  activeSection,
  setActiveSection,
  children,
}) {
  return freeTextJobSearch.value ? (
    <SingleGroupMonitorDataLoader
      sort={sort}
      freeTextJobSearch={freeTextJobSearch}
      isDetailOpen={isDetailOpen}
    >
      {children}
    </SingleGroupMonitorDataLoader>
  ) : (
    <GroupedMonitorDataLoader
      sort={sort}
      filters={filters}
      isDetailOpen={isDetailOpen}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
    >
      {children}
    </GroupedMonitorDataLoader>
  );
}

function SetActiveOnClose({ jobNumber, activeSection }) {
  const { setActiveJob, setActiveSection } = useContext(MonitorContext);
  useMountEffect(() => () => {
    setActiveJob(jobNumber);
    if (activeSection) {
      setActiveSection(activeSection);
    }
  });

  return null;
}

function ActiveItemLoader({ referenceNo, section }) {
  const { activeJob, activeSection, data } = useContext(MonitorContext);
  const { loadingPage1, loadAllItems, loading } = data[section] || {};

  useEffect(() => {
    const needsReload =
      activeJob && !some(data[section]?.rows, r => r.referenceNo === activeJob);
    if (
      loadAllItems &&
      !loadingPage1 &&
      needsReload &&
      activeSection === section &&
      !loading
    ) {
      loadAllItems();
    }
  }, [
    activeJob,
    activeSection,
    data,
    loadAllItems,
    loading,
    loadingPage1,
    section,
  ]);

  return null;
}

export default function MonitorPage() {
  const { styles: baseLoggedPageStyles } = useBaseLoggedPageStyles();
  const { styles } = useMonitorPageStyles(baseLoggedPageStyles);
  const { settings, loading } = useSettings();
  const media = useResponsiveQueries();
  const isLg = media.xxl || media.xl || media.lg;
  const isMd = media.md;
  const isSm = media.sm || media.xs;

  const [view, setView] = useState(VIEWS[0].name);
  const [activeSection, setActiveSection] = useState('live');
  const filtersAndSort = useFiltersAndSort({
    initialFilters: { stage: 'live' },
  });
  const freeTextJobSearch = useFreeTextJobSearch();

  const sort = useDebouncedVariable(
    monitorSortFormToGraphQL(filtersAndSort.sort.values),
    { comparator: isEqual }
  );

  const isDetailOpen = !!useRouteMatch(routes.shipmentDetail);

  useEffect(() => {
    const { defaultView } = settings || {};
    if (defaultView && defaultView !== view) {
      setView(defaultView);
    }
    // eslint-disable-next-line
  }, [settings]);

  return (
    <FiltersAndSortContext.Provider value={filtersAndSort}>
      <FreeTextJobSearchContext.Provider value={freeTextJobSearch}>
        <SplitterLeftPanelSizeProvider>
          <MonitorDataLoader
            sort={sort}
            filters={filtersAndSort.filters.values}
            freeTextJobSearch={freeTextJobSearch}
            isDetailOpen={isDetailOpen}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          >
            <ActiveItemLoader section="active" />
            <AutoViewSwitcher view={view} setView={setView} />
            <BaseLoggedPage
              id="MonitorPage"
              className={styles.monitorPage}
              wrapInScrollbar={isMd || isSm}
            >
              {!loading && (
                <>
                  <TitleWithExtra
                    titleId="monitor.title"
                    className={styles.searchTitleRow}
                    rightColClassName={styles.searchTitleCol}
                  >
                    <FreeTextJobSearch className={styles.searchInput} />
                  </TitleWithExtra>
                  {isLg && <ContentLarge view={view} setView={setView} />}
                  {isMd && <ContentMedium view={view} setView={setView} />}
                  {isSm && <ContentSmall view={view} setView={setView} />}
                </>
              )}
            </BaseLoggedPage>
            <Route path={routes.shipmentDetail}>
              {routeProps => (
                <TransitionGroup>
                  {routeProps.match && (
                    <CSSTransition
                      key="detail"
                      timeout={msToNumber(cssVariables.animationDurationMd)}
                      classNames="from-left"
                    >
                      <>
                        <ShipmentDetailPage {...routeProps} />
                        <SetActiveOnClose
                          jobNumber={routeProps.match.params.jobNumber}
                        />
                      </>
                    </CSSTransition>
                  )}
                </TransitionGroup>
              )}
            </Route>
            <Route path={routes.releaseShipment}>
              {routeProps => (
                <TransitionGroup>
                  {routeProps.match && (
                    <CSSTransition
                      key="release"
                      timeout={msToNumber(cssVariables.animationDurationMd)}
                      classNames="from-left"
                    >
                      <>
                        <ReleaseShipmentPage {...routeProps} />
                        <SetActiveOnClose
                          jobNumber={routeProps.match.params.jobNumber}
                        />
                      </>
                    </CSSTransition>
                  )}
                </TransitionGroup>
              )}
            </Route>
            <Route path={routes.quoting.quoteDetail}>
              {routeProps => (
                <TransitionGroup>
                  {routeProps.match && (
                    <CSSTransition
                      key="release"
                      timeout={msToNumber(cssVariables.animationDurationMd)}
                      classNames="from-left"
                    >
                      <>
                        <QuoteResultPage />
                        <SetActiveOnClose
                          jobNumber={routeProps.match.params.referenceNo}
                          activeSection="active"
                        />
                      </>
                    </CSSTransition>
                  )}
                </TransitionGroup>
              )}
            </Route>
          </MonitorDataLoader>
        </SplitterLeftPanelSizeProvider>
      </FreeTextJobSearchContext.Provider>
    </FiltersAndSortContext.Provider>
  );
}
